<script>
import { useVuelidate } from '@vuelidate/core';
import { required, minLength, helpers } from '@vuelidate/validators';
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';
import WhatsappOutgoingApi from 'dashboard/api/whatsappOutgoing';

const KEY_TYPES = ['CPF', 'CNPJ', 'PHONE', 'EMAIL', 'RANDOM'];

const generateReferenceId = () => {
  const ts = Date.now().toString(36).toUpperCase();
  const rand = Math.random().toString(36).slice(2, 8).toUpperCase();
  return `GK-${ts}-${rand}`;
};

const parseAmountToCents = value => {
  if (value === null || value === undefined || value === '') return 0;
  const normalized = String(value).replace(/\./g, '').replace(',', '.');
  const num = Number.parseFloat(normalized);
  if (Number.isNaN(num)) return 0;
  return Math.round(num * 100);
};

export default {
  components: { NextButton },
  props: {
    show: { type: Boolean, default: false },
    conversationId: { type: [Number, String], required: true },
  },
  emits: ['cancel', 'update:show', 'sent'],
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      form: {
        body_text: '',
        reference_id: '',
        pix_code: '',
        amount: '',
        item_name: '',
        merchant_name: '',
        pix_key: '',
        key_type: 'CNPJ',
      },
      isSubmitting: false,
      keyTypes: KEY_TYPES,
    };
  },
  validations() {
    return {
      form: {
        pix_code: { required, minLength: minLength(10) },
        amount: {
          required,
          positive: helpers.withMessage(
            this.$t(
              'CONVERSATION.ORDER_DETAILS.FORM.AMOUNT.VALIDATIONS.POSITIVE'
            ),
            v => parseAmountToCents(v) > 0
          ),
        },
        merchant_name: { required },
        pix_key: { required },
        key_type: { required },
      },
    };
  },
  computed: {
    ...mapGetters({ currentAccount: 'getCurrentAccount' }),
    localShow: {
      get() {
        return this.show;
      },
      set(value) {
        this.$emit('update:show', value);
      },
    },
    isFormValid() {
      return !this.v$.$invalid;
    },
  },
  watch: {
    show: {
      immediate: true,
      handler(value) {
        if (value) this.resetForm();
      },
    },
  },
  methods: {
    resetForm() {
      const attrs = this.currentAccount?.custom_attributes || {};
      this.form = {
        body_text: this.$t('CONVERSATION.ORDER_DETAILS.FORM.BODY_TEXT.DEFAULT'),
        reference_id: generateReferenceId(),
        pix_code: '',
        amount: '',
        item_name: this.$t('CONVERSATION.ORDER_DETAILS.FORM.ITEM_NAME.DEFAULT'),
        merchant_name: attrs.pix_merchant_name || '',
        pix_key: attrs.pix_key || '',
        key_type: attrs.pix_key_type || 'CNPJ',
      };
      this.v$.$reset();
    },
    onCancel() {
      this.$emit('cancel');
    },
    async onSubmit() {
      const valid = await this.v$.$validate();
      if (!valid) return;
      this.isSubmitting = true;
      try {
        const payload = {
          body_text: this.form.body_text,
          reference_id: this.form.reference_id,
          pix_code: this.form.pix_code.trim(),
          amount_cents: parseAmountToCents(this.form.amount),
          item_name: this.form.item_name,
          merchant_name: this.form.merchant_name,
          pix_key: this.form.pix_key,
          key_type: this.form.key_type,
        };
        const response = await WhatsappOutgoingApi.sendOrderDetails(
          this.conversationId,
          payload
        );
        useAlert(this.$t('CONVERSATION.ORDER_DETAILS.SEND_SUCCESS'));
        this.$emit('sent', response.data?.message);
        this.onCancel();
      } catch (error) {
        const apiMessage =
          error?.response?.data?.error ||
          this.$t('CONVERSATION.ORDER_DETAILS.SEND_ERROR');
        useAlert(apiMessage);
      } finally {
        this.isSubmitting = false;
      }
    },
  },
};
</script>

<template>
  <woot-modal
    v-model:show="localShow"
    :on-close="onCancel"
    size="modal-default"
  >
    <div class="flex flex-col h-auto overflow-auto">
      <woot-modal-header
        :header-title="$t('CONVERSATION.ORDER_DETAILS.MODAL.TITLE')"
        :header-content="$t('CONVERSATION.ORDER_DETAILS.MODAL.SUBTITLE')"
      />
      <form class="w-full px-8 pb-6" @submit.prevent="onSubmit">
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <label class="col-span-2">
            <span class="block text-sm font-medium mb-1 text-n-slate-12">
              {{ $t('CONVERSATION.ORDER_DETAILS.FORM.BODY_TEXT.LABEL') }}
            </span>
            <textarea
              v-model="form.body_text"
              rows="2"
              class="w-full"
              :placeholder="
                $t('CONVERSATION.ORDER_DETAILS.FORM.BODY_TEXT.PLACEHOLDER')
              "
            />
          </label>

          <label>
            <span class="block text-sm font-medium mb-1 text-n-slate-12">
              {{ $t('CONVERSATION.ORDER_DETAILS.FORM.AMOUNT.LABEL') }}
            </span>
            <input
              v-model="form.amount"
              type="text"
              inputmode="decimal"
              :placeholder="
                $t('CONVERSATION.ORDER_DETAILS.FORM.AMOUNT.PLACEHOLDER')
              "
              @blur="v$.form.amount.$touch"
            />
            <span v-if="v$.form.amount.$error" class="text-xs text-n-ruby-10">
              {{
                v$.form.amount.positive?.$invalid
                  ? $t(
                      'CONVERSATION.ORDER_DETAILS.FORM.AMOUNT.VALIDATIONS.POSITIVE'
                    )
                  : $t(
                      'CONVERSATION.ORDER_DETAILS.FORM.AMOUNT.VALIDATIONS.REQUIRED'
                    )
              }}
            </span>
          </label>

          <label>
            <span class="block text-sm font-medium mb-1 text-n-slate-12">
              {{ $t('CONVERSATION.ORDER_DETAILS.FORM.REFERENCE_ID.LABEL') }}
            </span>
            <input
              v-model="form.reference_id"
              type="text"
              :placeholder="
                $t('CONVERSATION.ORDER_DETAILS.FORM.REFERENCE_ID.PLACEHOLDER')
              "
            />
          </label>

          <label class="col-span-2">
            <span class="block text-sm font-medium mb-1 text-n-slate-12">
              {{ $t('CONVERSATION.ORDER_DETAILS.FORM.PIX_CODE.LABEL') }}
            </span>
            <textarea
              v-model="form.pix_code"
              rows="3"
              class="w-full font-mono text-xs"
              :placeholder="
                $t('CONVERSATION.ORDER_DETAILS.FORM.PIX_CODE.PLACEHOLDER')
              "
              @blur="v$.form.pix_code.$touch"
            />
            <span v-if="v$.form.pix_code.$error" class="text-xs text-n-ruby-10">
              {{
                $t(
                  'CONVERSATION.ORDER_DETAILS.FORM.PIX_CODE.VALIDATIONS.REQUIRED'
                )
              }}
            </span>
          </label>

          <label class="col-span-2">
            <span class="block text-sm font-medium mb-1 text-n-slate-12">
              {{ $t('CONVERSATION.ORDER_DETAILS.FORM.ITEM_NAME.LABEL') }}
            </span>
            <input
              v-model="form.item_name"
              type="text"
              :placeholder="
                $t('CONVERSATION.ORDER_DETAILS.FORM.ITEM_NAME.PLACEHOLDER')
              "
            />
          </label>

          <label class="col-span-2">
            <span class="block text-sm font-medium mb-1 text-n-slate-12">
              {{ $t('CONVERSATION.ORDER_DETAILS.FORM.MERCHANT_NAME.LABEL') }}
            </span>
            <input
              v-model="form.merchant_name"
              type="text"
              :placeholder="
                $t('CONVERSATION.ORDER_DETAILS.FORM.MERCHANT_NAME.PLACEHOLDER')
              "
              @blur="v$.form.merchant_name.$touch"
            />
            <span
              v-if="v$.form.merchant_name.$error"
              class="text-xs text-n-ruby-10"
            >
              {{
                $t(
                  'CONVERSATION.ORDER_DETAILS.FORM.MERCHANT_NAME.VALIDATIONS.REQUIRED'
                )
              }}
            </span>
          </label>

          <label>
            <span class="block text-sm font-medium mb-1 text-n-slate-12">
              {{ $t('CONVERSATION.ORDER_DETAILS.FORM.KEY_TYPE.LABEL') }}
            </span>
            <select v-model="form.key_type">
              <option v-for="t in keyTypes" :key="t" :value="t">{{ t }}</option>
            </select>
          </label>

          <label>
            <span class="block text-sm font-medium mb-1 text-n-slate-12">
              {{ $t('CONVERSATION.ORDER_DETAILS.FORM.PIX_KEY.LABEL') }}
            </span>
            <input
              v-model="form.pix_key"
              type="text"
              :placeholder="
                $t('CONVERSATION.ORDER_DETAILS.FORM.PIX_KEY.PLACEHOLDER')
              "
              @blur="v$.form.pix_key.$touch"
            />
            <span v-if="v$.form.pix_key.$error" class="text-xs text-n-ruby-10">
              {{
                $t(
                  'CONVERSATION.ORDER_DETAILS.FORM.PIX_KEY.VALIDATIONS.REQUIRED'
                )
              }}
            </span>
          </label>
        </div>

        <div class="flex flex-row justify-end w-full gap-2 mt-6">
          <NextButton
            faded
            slate
            type="reset"
            :label="$t('CONVERSATION.ORDER_DETAILS.MODAL.CANCEL')"
            @click.prevent="onCancel"
          />
          <NextButton
            type="submit"
            :label="$t('CONVERSATION.ORDER_DETAILS.MODAL.SEND')"
            :disabled="!isFormValid || isSubmitting"
            :is-loading="isSubmitting"
          />
        </div>
      </form>
    </div>
  </woot-modal>
</template>
